import EditableList from '@/components/EditableList'
import SimpleGroupCard from '@/components/SimpleGroupCard'
import { useFetchProfile } from '@/hooks/useFetchProfile'
import { normalizeUrl } from '@/lib/url'
import { username } from '@/lib/event-metadata'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { useAtomValue } from 'jotai'
import { invalidateSimpleGroups, simpleGroupsChannelAtom } from '@/atoms/invalidation'
import client from '@/services/client.service'
import { loadSimpleGroups, SimpleGroupItem } from '@nostr/gadgets/lists'
import * as kinds from '@nostr/tools/kinds'
import { now } from '@/lib/utils'
import { forwardRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAsync } from 'react-use'
import { useCurrentPubkey } from '@/atoms/session'
import { sampleTopFromFollows } from '@/lib/recommendations'
import { unwrappedSimpleGroupsAtom } from '@/atoms/lists'

const SimpleGroupsPage = forwardRef(({ id, index }: { id: string; index?: number }, ref) => {
  const { t } = useTranslation()
  const currentPubkey = useCurrentPubkey()
  const { profile } = useFetchProfile(id)
  const channel = useAtomValue(simpleGroupsChannelAtom)
  const ourSimpleGroups = useAtomValue(unwrappedSimpleGroupsAtom) ?? []

  useEffect(() => {
    if (!profile) return
    const name = profile.shortName || username(profile) || '...'
    document.title = t("{{name}}'s simple groups on {{appName}}", {
      name,
      appName: window.hallway.universe.appName
    })
    history.replaceState(null, '')
  }, [profile])
  const { value: groups } = useAsync(
    async () =>
      profile
        ? profile.pubkey === currentPubkey
          ? ourSimpleGroups
          : (await loadSimpleGroups(profile.pubkey)).items
        : [],
    [profile?.pubkey, channel]
  )

  async function loadRecommendations(): Promise<SimpleGroupItem[]> {
    if (!currentPubkey || !groups) return []
    const existing = new Set(groups.map((g) => `${g.groupId}:${g.relay}`))
    return sampleTopFromFollows<SimpleGroupItem>(
      currentPubkey,
      async (pk) => (await loadSimpleGroups(pk)).items,
      (g) => `${g.groupId}:${g.relay}`,
      existing
    )
  }

  return (
    <SecondaryPageLayout
      ref={ref}
      index={index}
      title={
        profile && profile.pubkey !== currentPubkey
          ? t("{{username}}'s simple groups", { username: username(profile) })
          : t('Simple Groups')
      }
    >
      <div className="px-4 py-3">
        <EditableList
          items={groups || []}
          editable={profile?.pubkey === currentPubkey}
          keyExtractor={(g) => `${g.groupId}:${g.relay}`}
          renderItem={(group) => <SimpleGroupCard group={group} />}
          validateItem={(val: string) => {
            if (!currentPubkey) return { ok: false as const, error: t('Login required') }
            const parts = val.trim().split(/\s+/)
            if (parts.length < 2)
              return { ok: false as const, error: t('Enter group ID and relay URL') }
            const groupId = parts[0]
            if (groupId.length !== 64 || !/^[0-9a-f]{64}$/i.test(groupId))
              return { ok: false as const, error: t('Invalid group ID') }
            const normalized = normalizeUrl(parts.slice(1).join(' '))
            if (!normalized) return { ok: false as const, error: t('Invalid relay URL') }
            const name = parts.length > 2 ? parts.slice(2).join(' ') : undefined
            if ((groups || []).some((g) => g.groupId === groupId && g.relay === normalized))
              return { ok: false as const, error: t('Group already exists') }
            return {
              ok: true as const,
              item: { groupId, relay: normalized, name }
            }
          }}
          onUpdate={async (newGroups: SimpleGroupItem[]) => {
            if (!currentPubkey) return
            const event = await client.publish({
              kind: kinds.SimpleGroupList,
              content: '',
              tags: newGroups.map((g) =>
                g.name ? ['group', g.groupId, g.relay, g.name] : ['group', g.groupId, g.relay]
              ),
              created_at: now()
            })
            if (!event) {
              console.warn('failed to publish updated simple groups list event')
              toast.error(t('Failed to publish updated simple groups list event.'))
              return
            }
            await loadSimpleGroups(event.pubkey, undefined, event)
            invalidateSimpleGroups()
          }}
          placeholder={t('Enter group ID and relay URL')}
          emptyMessage={t('No simple groups joined.')}
          loadRecommendations={loadRecommendations}
        />
      </div>
    </SecondaryPageLayout>
  )
})
SimpleGroupsPage.displayName = 'SimpleGroupsPage'
export default SimpleGroupsPage
